import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import {
  usePutAttendance,
  usePatchDecision,
  usePatchNotified,
  useEntities,
  useDecisionTypes,
  type DailyRosterEntry,
} from '../api/hooks.js';
import {
  DECISION_MENU,
  attendanceWriteError,
  buildDecisionMenu,
  recommendedOption,
  hhmmFromMinutes,
  clockToMinutes,
  isPendingRow,
  avatarColor,
  type DecisionOption,
} from '../lib/saisie.js';
import { fmtFrShortDow } from '../lib/date.js';
import { toast } from '../store/toast.js';
import { useUnsavedGuard } from '../lib/unsaved.js';
import { PunchTime } from './PunchTime.js';

// Guided, one-exception-at-a-time arbitrage. Keyboard-first. Decisions
// are provisional (PATCH /decision [+ status for ½, + notified]); the
// leave ledger only moves at validate-day, so NO copy here claims the
// balance changed.

interface Props {
  date: string;
  rows: DailyRosterEntry[];
  startEmployeeId?: string;
  onExit: () => void;
}

function initials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

export function ArbitrageView({ date, rows, startEmployeeId, onExit }: Props) {
  const { data: entities } = useEntities();
  const nameByEntity = useMemo(
    () => new Map((entities ?? []).map((e) => [e.id, e.name])),
    [entities],
  );

  // The menu = the trimmed built-ins + whatever types the team defined in
  // Paramètres. Built-ins render first and keep their letter shortcuts.
  const { data: decisionTypes } = useDecisionTypes();
  const menu = useMemo(() => buildDecisionMenu(decisionTypes), [decisionTypes]);
  const customOptions = useMemo(() => menu.slice(DECISION_MENU.length), [menu]);

  const put = usePutAttendance();
  const decision = usePatchDecision();
  const notified = usePatchNotified();

  // Freeze the queue order once (so resolved rows keep their slot rather
  // than the list reshuffling). Normally the pending rows; if the user
  // clicked a settled row to override, inject it so the flow focuses it.
  const [order] = useState<string[]>(() => {
    const ids = rows.filter(isPendingRow).map((r) => r.employee.id);
    if (startEmployeeId && !ids.includes(startEmployeeId) && rows.some((r) => r.employee.id === startEmployeeId)) {
      ids.unshift(startEmployeeId);
    }
    return ids;
  });
  const total = order.length;

  const [idx, setIdx] = useState(() => {
    const i = startEmployeeId ? order.indexOf(startEmployeeId) : 0;
    return i < 0 ? 0 : i;
  });

  const byId = useMemo(() => new Map(rows.map((r) => [r.employee.id, r])), [rows]);

  // "Done" = committed this session. Seed with rows already settled at
  // mount, except a settled row the user opened to override (stays live).
  const [committedIds, setCommittedIds] = useState<Set<string>>(() => {
    const s = new Set<string>();
    for (const id of order) {
      if (id === startEmployeeId) continue;
      const r = rows.find((x) => x.employee.id === id);
      if (r && !isPendingRow(r)) s.add(id);
    }
    return s;
  });
  const doneSet = committedIds;

  const current = byId.get(order[idx] ?? '') ?? null;
  const noClock = !current?.record?.checkIn && !current?.record?.checkOut;

  // Per-row UI state.
  const [pick, setPickRaw] = useState<DecisionOption>(() => recommendedOption(noClock));
  // Proto default: « Oui » pre-selected (green) until the user says otherwise.
  const [prevenu, setPrevenuRaw] = useState<'YES' | 'NO' | null>('YES');
  const [note, setNoteRaw] = useState('');
  const [committed, setCommitted] = useState(false);
  // Vrai dès que l'utilisateur a touché la carte : c'est ce brouillon-là
  // que le garde-fou de sortie protège (Abandonner / Rester).
  const [touched, setTouched] = useState(false);
  const setPick = (v: DecisionOption) => { setTouched(true); setPickRaw(v); };
  const setPrevenu = (v: 'YES' | 'NO' | null) => { setTouched(true); setPrevenuRaw(v); };
  const setNote = (v: string) => { setTouched(true); setNoteRaw(v); };
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const resetCard = () => {
    setPickRaw(recommendedOption(noClock));
    setPrevenuRaw(current?.record?.notifiedInAdvance ?? 'YES');
    setNoteRaw(current?.record?.decisionNote ?? '');
    setCommitted(false);
    setTouched(false);
  };

  // Reset when the focused employee changes.
  const curId = current?.employee.id;
  useEffect(() => {
    resetCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curId]);

  useUnsavedGuard({
    id: 'arbitrage-card',
    dirty: touched && !committed,
    label: 'Arbitrage en cours',
    summary: current?.employee.name,
    discard: resetCard,
  });

  // Exit when nothing is pending anymore.
  useEffect(() => {
    if (total === 0 || doneSet.size >= total) {
      const t = setTimeout(onExit, 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [doneSet.size, total, onExit]);

  function advance() {
    // next not-done slot after idx, wrapping once.
    for (let step = 1; step <= total; step++) {
      const j = (idx + step) % total;
      const id = order[j];
      if (id && !doneSet.has(id)) {
        setIdx(j);
        return;
      }
    }
    onExit();
  }

  async function onCommit() {
    if (!current || committed) return;
    try {
      let id = current.record?.id ?? null;
      const wasHalf = current.record?.status === 'CONGE_HALF';
      const needStatusWrite = pick.status === 'CONGE_HALF' || wasHalf;
      const update = needStatusWrite ? { status: pick.status ?? 'AUTO' } : {};
      if (id == null || needStatusWrite) {
        const rec = await put.mutateAsync({ date, employeeId: current.employee.id, update });
        id = rec.id;
      }
      await decision.mutateAsync({
        recordId: id,
        date,
        decisionType: pick.decisionType,
        customCode: pick.customCode ?? null,
        decisionNote: note || null,
      });
      if (prevenu) {
        await notified.mutateAsync({ recordId: id, date, notifiedInAdvance: prevenu });
      }
      setCommitted(true);
      setCommittedIds((prev) => new Set(prev).add(current.employee.id));
      window.setTimeout(() => {
        setCommitted(false);
        advance();
      }, 420);
    } catch (err) {
      toast.danger(attendanceWriteError(err, "Échec de l'enregistrement de la décision."));
    }
  }

  function savePunch(field: 'checkIn' | 'checkOut', value: string) {
    if (!current) return;
    put.mutate(
      { date, employeeId: current.employee.id, update: { [field]: value } },
      {
        onSuccess: () => toast.success('Heure corrigée — l’heure pointeuse reste visible à côté.'),
        onError: (err) => toast.danger(attendanceWriteError(err, "Échec de la correction de l'heure.")),
      },
    );
  }

  // Keyboard — suppressed while the note textarea (or a punch being
  // corrected) is focused.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = document.activeElement;
      const inField = el === noteRef.current || el?.tagName === 'INPUT';
      if (e.key === 'Escape') {
        if (inField) noteRef.current?.blur();
        else onExit();
        return;
      }
      if (inField) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        void onCommit();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        advance();
        return;
      }
      const opt = menu.find((o) => o.hotkey === e.key.toUpperCase());
      if (opt) {
        e.preventDefault();
        setPick(opt);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, pick, prevenu, note, committed, idx, menu]);

  if (!current) {
    return (
      <div className="sq-view">
        <p className="page-placeholder">Aucune exception à arbitrer.</p>
        <button type="button" className="btn btn-sm" onClick={onExit}>
          <ArrowLeft aria-hidden="true" /> Retour au tableau
        </button>
      </div>
    );
  }

  const resolvedCount = doneSet.size;
  const flag = computeFlag(current);
  const tl = computeTimeline(current);
  const markers = buildMarkers(current, tl);
  const entityName = nameByEntity.get(current.employee.entityId);
  const role = current.employee.role?.trim();

  return (
    <div className="sq-view" data-testid="arbitrage-view">
      {/* top bar */}
      <div className="sq-arb-topbar">
        <button type="button" className="btn btn-sm" onClick={onExit} data-testid="arb-back">
          <ArrowLeft aria-hidden="true" /> Retour au tableau
        </button>
        <span className="sq-arb-chip">⚡ Mode arbitrage</span>
        <span className="sq-arb-progress">
          Exception <strong>{Math.min(resolvedCount + 1, total)}/{total}</strong>
          <span className="sq-arb-dots">
            {order.map((id, i) => (
              <span
                key={id}
                className={`sq-arb-dot ${doneSet.has(id) ? 'done' : i === idx ? 'current' : ''}`}
              />
            ))}
          </span>
        </span>
        <span className="sq-banner-spacer" />
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }} data-testid="arb-date">
          {fmtFrShortDow(date)}{entityName ? ` · ${entityName}` : ''}
        </span>
        <button type="button" className="btn btn-sm sq-arb-exit" onClick={onExit} data-testid="arb-quit">
          Quitter <kbd>Esc</kbd>
        </button>
      </div>

      <div className="sq-arb-bar">
        <div className="sq-arb-bar-fill" style={{ width: `${total ? (resolvedCount / total) * 100 : 0}%` }} />
      </div>

      <div className="sq-arb-stage">
        {/* queue rail */}
        <div>
          <div className="sq-queue-label">File d'exceptions</div>
          <div className="sq-queue">
            {order.map((id, i) => {
              const r = byId.get(id);
              if (!r) return null;
              const done = doneSet.has(id);
              const cls = done ? 'done' : i === idx ? 'current' : 'pending';
              return (
                <button
                  key={id}
                  type="button"
                  className={`sq-queue-card ${cls}`}
                  onClick={() => setIdx(i)}
                >
                  <span
                    className={`sq-avatar sm ${done ? 'done' : ''}`}
                    style={{ background: avatarColor(r.employee.id) }}
                  >
                    {done ? <Check size={14} aria-hidden="true" /> : initials(r.employee.name)}
                  </span>
                  <span>
                    <span className="sq-queue-name">{r.employee.name.split(' ')[0]}</span>
                    <span className="sq-queue-sub" style={{ display: 'block' }}>
                      {done ? 'traité' : shortIssue(r)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="sq-queue-foot">{Math.max(total - resolvedCount, 0)} restante(s)</div>
        </div>

        {/* focus card */}
        <div className="sq-focus" data-testid="arb-focus">
          {committed && (
            <div className="sq-confirm">
              <Check aria-hidden="true" /> Décision enregistrée
            </div>
          )}

          <div className="sq-focus-head">
            <span
              className="sq-avatar lg"
              style={{ background: avatarColor(current.employee.id) }}
            >
              {initials(current.employee.name)}
            </span>
            <div>
              <div className="sq-focus-name">{current.employee.name}</div>
              <div className="sq-focus-role">
                {role ? (
                  <>
                    {role} · <span className="sq-focus-ent">{entityName ?? 'SHIFTIN'}</span>
                  </>
                ) : entityName ? (
                  <span className="sq-focus-ent">{entityName}</span>
                ) : (
                  current.employee.code
                )}
              </div>
            </div>
            <span className={`sq-flag tone-${flag.tone}`}>
              <span className="sq-flag-big">{flag.big}</span>
              <span className="sq-flag-lbl">{flag.lbl}</span>
            </span>
          </div>

          {/* hero timeline */}
          <div className="sq-timeline">
            <div className="sq-track">
              {tl.fill && <div className="sq-track-fill" style={{ left: `${tl.fill[0]}%`, width: `${tl.fill[1] - tl.fill[0]}%` }} />}
              {tl.band && <div className={`sq-track-band ${flag.tone === 'bad' ? 'bad' : 'warn'}`} style={{ left: `${tl.band[0]}%`, width: `${tl.band[1] - tl.band[0]}%` }} />}
              {markers.map((m, i) => (
                <div key={i} className={`sq-marker ${m.cls}`} style={{ left: `${m.pct}%` }} data-testid={`arb-marker-${m.cls}`}>
                  <span className="sq-marker-time">{m.time}</span>
                  <span className="sq-marker-sub">{m.sub}</span>
                  <i className="sq-marker-dot" aria-hidden="true" />
                </div>
              ))}
            </div>
            <div className="sq-scale">
              {tl.ticks.map((t, i) => (
                <span key={i}>{t}</span>
              ))}
            </div>
            <div className="sq-readout">
              {/* A wrong badge is often why the row is here at all —
                  correct it on the spot, the card re-reads the day. */}
              <div className="sq-readout-cell">
                <div className="sq-readout-val">
                  <PunchTime
                    label="entrée"
                    size="hero"
                    value={current.record?.checkIn ?? null}
                    device={current.record?.checkInDevice ?? null}
                    manual={current.record?.checkInManual ?? false}
                    editable
                    pending={put.isPending}
                    onSave={(v) => savePunch('checkIn', v)}
                    testid="arb-punch-in"
                  />
                </div>
                <div className="sq-readout-lbl">entrée</div>
              </div>
              <div className="sq-readout-cell">
                <div className="sq-readout-val">
                  <PunchTime
                    label="sortie"
                    size="hero"
                    value={current.record?.checkOut ?? null}
                    device={current.record?.checkOutDevice ?? null}
                    manual={current.record?.checkOutManual ?? false}
                    editable
                    pending={put.isPending}
                    onSave={(v) => savePunch('checkOut', v)}
                    testid="arb-punch-out"
                  />
                </div>
                <div className="sq-readout-lbl">sortie</div>
              </div>
              <div className="sq-readout-cell">
                <div className="sq-readout-val">{hhmmFromMinutes(current.recordStatus.durationMinutes)}</div>
                <div className="sq-readout-lbl">travaillées</div>
              </div>
              <div className="sq-readout-cell hot">
                <div className="sq-readout-val">{flag.big}</div>
                <div className="sq-readout-lbl">{flag.lbl}</div>
              </div>
            </div>
          </div>

          {/* note */}
          <div className="sq-note-field">
            <label htmlFor="arb-note">Note <span>· contexte, justificatif, suivi (optionnel)</span></label>
            <textarea
              id="arb-note"
              ref={noteRef}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ex. justificatif médical attendu…"
              data-testid="arb-note"
            />
          </div>

          {/* decision grid */}
          <div className="sq-decide-h">
            Quelle décision&nbsp;? <span>clique ou tape la touche</span>
          </div>
          <div className="sq-grid" data-testid="arb-grid">
            {DECISION_MENU.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`sq-opt tone-${o.tone} ${pick.key === o.key ? 'sel' : ''}`}
                onClick={() => setPick(o)}
                data-testid={`arb-opt-${o.key}`}
              >
                <span className="sq-opt-key">{o.hotkey ?? ''}</span>
                <div className="sq-opt-title">{o.title}</div>
                <div className="sq-opt-desc">{o.desc}</div>
              </button>
            ))}
          </div>

          {customOptions.length > 0 && (
            <>
              <div className="sq-decide-h sq-decide-h-sub">
                Types personnalisés <span>définis dans Paramètres</span>
              </div>
              <div className="sq-grid" data-testid="arb-grid-custom">
                {customOptions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={`sq-opt tone-${o.tone} ${pick.key === o.key ? 'sel' : ''}`}
                    onClick={() => setPick(o)}
                    data-testid={`arb-opt-${o.key}`}
                  >
                    <span className="sq-opt-key">{o.hotkey ?? ''}</span>
                    <div className="sq-opt-title">{o.title}</div>
                    <div className="sq-opt-desc">{o.desc}</div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* footer */}
          <div className="sq-arb-foot">
            <span className="sq-seg-label">Salarié prévenu&nbsp;?</span>
            <span className="sq-seg">
              <button type="button" className={prevenu === 'YES' ? 'on-yes' : ''} onClick={() => setPrevenu('YES')}>
                Oui
              </button>
              <button type="button" className={prevenu === 'NO' ? 'on-no' : ''} onClick={() => setPrevenu('NO')}>
                Non
              </button>
            </span>
            <span className="sq-banner-spacer" />
            <button type="button" className="btn btn-sm" onClick={advance} data-testid="arb-skip">
              Ignorer ↓
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => void onCommit()}
              disabled={put.isPending || decision.isPending}
              data-testid="arb-commit"
            >
              <Check aria-hidden="true" /> Valider &amp; suivant ↵
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── derivations ──────────────────────────────────────────────
interface Flag { big: string; lbl: string; tone: 'warn' | 'bad'; }

function computeFlag(r: DailyRosterEntry): Flag {
  const noClock = !r.record?.checkIn && !r.record?.checkOut;
  const late = r.tags.find((t) => t.type === 'late');
  const less = r.tags.find((t) => t.type === 'less');
  if (noClock) return { big: '0:00', lbl: 'aucun pointage', tone: 'bad' };
  if (late?.delta != null) return { big: `${Math.round(late.delta)} min`, lbl: 'de retard', tone: 'warn' };
  if (less?.delta != null) return { big: hhmmFromMinutes(r.recordStatus.durationMinutes), lbl: `−${hhmmFromMinutes(Math.abs(less.delta))} manquantes`, tone: 'warn' };
  return { big: hhmmFromMinutes(r.recordStatus.durationMinutes), lbl: 'à arbitrer', tone: 'warn' };
}

function shortIssue(r: DailyRosterEntry): string {
  if (!r.record?.checkIn && !r.record?.checkOut) return 'absent';
  const late = r.tags.find((t) => t.type === 'late');
  if (late) return late.label;
  const less = r.tags.find((t) => t.type === 'less');
  if (less) return 'journée courte';
  if (r.needsNotified) return 'prévenu ?';
  return 'à arbitrer';
}

interface TL {
  planPct: number;
  inPct: number | null;
  outPct: number | null;
  fill: [number, number] | null;
  band: [number, number] | null;
  /** span bounds in minutes-from-midnight (for the dynamic scale). */
  spanStart: number;
  spanEnd: number;
  /** evenly-spaced HH tick labels across the span. */
  ticks: string[];
}

export function computeTimeline(r: DailyRosterEntry): TL {
  // §9.2: real schedule from the payload, with a graceful 08:00 / 8h
  // fallback (never NaN) when an entity has no planning defined.
  const planMin = clockToMinutes(r.expectedStart) ?? 8 * 60;
  const eh = r.expectedHours;
  const expectedHours = typeof eh === 'number' && Number.isFinite(eh) && eh > 0 ? eh : 8;
  const expectedEndMin = planMin + expectedHours * 60;
  const inMin = clockToMinutes(r.record?.checkIn);
  const outMin = clockToMinutes(r.record?.checkOut);

  // §P1.3: dynamic span instead of a frozen 07→18 track. Pad 1h before
  // the earliest of (prévu, entrée) and 1h after the latest of (sortie,
  // fin prévue), so a real 20:32 departure never clips off-scale.
  const starts = [planMin, inMin].filter((v): v is number => v != null);
  const ends = [expectedEndMin, outMin].filter((v): v is number => v != null);
  const spanStart = Math.min(...starts) - 60;
  const spanEnd = Math.max(...ends) + 60;
  const span = Math.max(spanEnd - spanStart, 60);
  const pct = (m: number) => Math.max(0, Math.min(100, ((m - spanStart) / span) * 100));

  const planPct = pct(planMin);
  const inPct = inMin != null ? pct(inMin) : null;
  const outPct = outMin != null ? pct(outMin) : null;
  const fill = inMin != null && outMin != null ? ([pct(inMin), pct(outMin)] as [number, number]) : null;

  let band: [number, number] | null = null;
  const late = r.tags.find((t) => t.type === 'late');
  const less = r.tags.find((t) => t.type === 'less');
  // late window: expected start → real arrival; missing tail: real departure → expected end.
  if (late && inMin != null) band = [pct(planMin), pct(inMin)];
  else if (less && outMin != null) band = [pct(outMin), pct(expectedEndMin)];

  // Five evenly-spaced HH:MM labels spanning the (padded) window (KEEP:
  // dynamic span, but ticks formatted HH:MM not bare HH).
  const ticks = Array.from({ length: 5 }, (_, i) => {
    const m = Math.round(spanStart + (span * i) / 4);
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(((m % 60) + 60) % 60).padStart(2, '0')}`;
  });
  return { planPct, inPct, outPct, fill, band, spanStart, spanEnd, ticks };
}

// Hero-timeline markers: labelled dots (time + caps sub-label) above the
// track (proto). plan = grey, late arrival / short departure = warn
// ('real'), an on-time departure = accent ('out').
interface Marker { pct: number; cls: 'plan' | 'real' | 'out'; time: string; sub: string; }
function buildMarkers(r: DailyRosterEntry, tl: TL): Marker[] {
  const hasLate = r.tags.some((t) => t.type === 'late');
  const hasLess = r.tags.some((t) => t.type === 'less');
  const out: Marker[] = [{ pct: tl.planPct, cls: 'plan', time: r.expectedStart ?? '08:00', sub: 'prévu' }];
  if (tl.inPct != null && r.record?.checkIn) {
    out.push({ pct: tl.inPct, cls: hasLate ? 'real' : 'plan', time: r.record.checkIn, sub: 'arrivée' });
  }
  if (tl.outPct != null && r.record?.checkOut) {
    out.push({ pct: tl.outPct, cls: hasLess ? 'real' : 'out', time: r.record.checkOut, sub: 'départ' });
  }
  return out;
}
